// MobileLayout.js
// Full redesign: a single vertical-scroll page (not tabs) mirroring the desktop's
// mockup-derived section order, in the same dark Twenty4Seven design language.
// WAN/VLAN rows open the same detail modal used on desktop — it becomes a bottom
// sheet automatically at this width (see t247Shared's responsive modal CSS).
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import FleetHeader from "../components/FleetHeader";
import DateSelector from "../components/DateSelector";
import PDFDownloadButton from "../components/PDFDownloadButton";
import RealtimeBandwidthChart from "../components/monitoring/RealtimeBandwidthChart";
import SpeedFusionLiveStatus from "../components/monitoring/SpeedFusionLiveStatus";
import LeafletMap, { hasLocationTrack } from "../components/monitoring/LeafletMap";
import CombinedHourlyGraph from "../components/monitoring/CombinedHourlyGraph";
import DailyBreakdownChart from "../components/monitoring/DailyBreakdownChart";
import T247WanModal from "../components/monitoring/T247WanModal";
import WanPriorityControl from "../components/monitoring/WanPriorityControl";
import { canChangePriorityForType, applyPendingToReport } from "../utils/wanPriority";
import T247VlanModal from "../components/monitoring/T247VlanModal";
import LogsDisplay from "../components/monitoring/LogsDisplay";

import {
  T247_THEME_CSS,
  buildDeviceData,
  computeWanRows,
  computeVlanRows,
  computeThroughput,
  computePingStats,
  fmt1,
  fmt2,
  safeArr,
  extractLogText,
  DualAreaSpark,
  AreaSpark,
  MiniBar,
  DonutChart,
} from "../components/monitoring/t247Shared";

function MobileLayout({
  user,
  userType,
  serialNumber,
  selectedDate,
  setSelectedDate,
  period = "daily",
  setPeriod,
  deviceData: rawDeviceData,
  isLoading,
  selectedDeviceId,
  isClient,
  alerts,
  vlanAlerts,
  wanCapAlerts,
  starlinkAlerts,
  isLoadingAlerts,
  isReportReady,
  shouldSkipStarlink,
  onLogout,
  managementType,
  ic2Writable,
  onPriorityChanged,
  starlinkLocation,
}) {
  const navigate = useNavigate();
  // Back to the fleet overview: clients -> ClientSiteDashboard (/dashboard),
  // admins -> SiteDashboard (/admin/dashboard). Same routes as App.jsx.
  const backPath = userType === "client" ? "/dashboard" : "/admin/dashboard";
  const goBack = () => navigate(backPath);
  const [selectedWan, setSelectedWan] = useState(null);
  const [selectedVlan, setSelectedVlan] = useState(null);
  const loadingVideo = process.env.PUBLIC_URL + '/Logo_video.mp4';
  const deviceData = buildDeviceData(rawDeviceData);

  // ── WAN priority change ─────────────────────────────────────────────────
  // Only internal admins and client admins (role === 'admin' - same rule as
  // SiteDashboard/ClientSiteDashboard canEdit; the backend enforces the same),
  // only on the daily view of TODAY (older dates / weekly / monthly are
  // historical report data - view only), and only for device types the
  // shared wanPriority module allows (DC blocked unless its switch is on).
  const todayISO = new Date().toISOString().split("T")[0];
  const isLiveView = period === "daily" && selectedDate === todayISO;
  const canChangePriority = user?.role === "admin" && isLiveView && ic2Writable && canChangePriorityForType(managementType);
  const priorityScope = userType === "client" ? "client" : "admin";

  const isOnline = deviceData?.lastStatus === "online";
  const hasPeplinkTrack = hasLocationTrack(deviceData?.locationMap);

  // Today's daily view shows just-submitted priority changes straight away
  // (same pending memory as LiveWanPanel). Historical views are untouched.
  const { rows: wanRows, totals: wanTotals, downCount } = computeWanRows(
    isLiveView ? applyPendingToReport(serialNumber, deviceData) : deviceData
  );
  const vlanRows = computeVlanRows(deviceData);
  const throughput = computeThroughput(deviceData, period);
  const pingStats = computePingStats(deviceData);

  const totalTrafficGB = wanTotals.total;
  const cellularGB = wanRows.filter((r) => r.isCellular).reduce((s, r) => s + r.total, 0);
  const cellularPct = totalTrafficGB > 0 ? (cellularGB / totalTrafficGB) * 100 : 0;
  const failoverEvents = safeArr(deviceData?.wanLogs).filter((l) => /fail|down/i.test(extractLogText(l))).length;
  const alertsCount = safeArr(alerts).length + safeArr(vlanAlerts).length + safeArr(wanCapAlerts).length + safeArr(starlinkAlerts).length;

  const allClients = deviceData ? [...safeArr(deviceData.clientUsages)].sort((a, b) => (b.total_usage || 0) - (a.total_usage || 0)) : [];
  const clientTotals = allClients.reduce(
    (acc, c) => ({
      download: acc.download + (c.download || 0) / 1024,
      upload: acc.upload + (c.upload || 0) / 1024,
      total: acc.total + (c.total_usage || 0) / 1024,
    }),
    { download: 0, upload: 0, total: 0 }
  );

  const wanDownloadSegments = wanRows.map((r) => ({ name: r.name, value: r.download }));
  const wanUploadSegments = wanRows.map((r) => ({ name: r.name, value: r.upload }));

  return (
    <div className="t247 t247m">
      <FleetHeader title="Monitoring" user={user} onLogout={onLogout} scope={userType} />

      {/* ── DEVICE BANNER (period / date / pdf) ────────────────────────── */}
      <div className="t247m-devicebar">
        <button type="button" className="t247-back-btn" onClick={goBack} aria-label="Back to Site Dashboard">
          <i className="fas fa-arrow-left"></i>
          SITE DASHBOARD
        </button>
        <div className="t247m-devicebar-identity" data-tour="mon-device">
          <div className="name">{deviceData?.deviceName || "Monitoring"}</div>
          <div className="serial">{serialNumber}</div>
          <span className={`t247-live-badge ${isOnline ? "" : "is-off"}`} style={{ marginTop: 8 }}>
            <span className="t247-dot" />
            {isOnline ? "ONLINE" : "OFFLINE"}
          </span>
        </div>
        <div className="t247-period-toggle" style={{ width: "100%", marginTop: 12 }} data-tour="mon-period">
          {[
            { key: "daily", label: "DAILY" },
            { key: "weekly", label: "WEEKLY" },
            { key: "monthly", label: "MONTHLY" },
          ].map((p) => (
            <button key={p.key} type="button" className={`t247-period-btn ${period === p.key ? "active" : ""}`} style={{ flex: 1 }} onClick={() => setPeriod && setPeriod(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 10 }} data-tour="mon-date">
          <DateSelector selectedDate={selectedDate} onDateChange={setSelectedDate} deviceId={selectedDeviceId} serialNumber={serialNumber} />
        </div>
        <div style={{ marginTop: 10, display: "flex", justifyContent: "center" }} data-tour="mon-pdf">
          <PDFDownloadButton
            deviceId={selectedDeviceId}
            period={period}
            date={selectedDate}
            deviceInfo={{ deviceName: deviceData?.deviceName, serialNumber: deviceData?.serialNumber }}
            isReportReady={isReportReady}
          />
        </div>
      </div>

      {isLoading && (
        <div className="cm-loading-overlay">
          <div className="cm-spinner-container">
            <video autoPlay loop muted playsInline preload="auto" poster={process.env.PUBLIC_URL + "/Logo_video_poster.jpg"} className="spinner-video" width="280" height="280">
              <source src={loadingVideo} type="video/mp4" />
            </video>
            <p>Loading device data…</p>
          </div>
        </div>
      )}

      {deviceData && !isLoading && (
        <div className="t247m-stack">
          {/* DEVICE STATE */}
          <div className="t247-panel" data-tour="mon-state">
            <div className="t247-panel-header">DEVICE STATE</div>
            <div className="t247-state-grid">
              <StateCell label="UPTIME" value={deviceData.uptime} />
              <StateCell label="CLIENTS" value={String(deviceData.clientCount || 0)} />
              <StateCell label="MODEL" value={deviceData.model || "—"} small />
              <StateCell label="FIRMWARE" value={deviceData.firmwareVersion || "—"} small />
            </div>
          </div>

          {/* TODAY AT A GLANCE */}
          <div className="t247-panel" data-tour="mon-glance">
            <div className="t247-panel-header">TODAY AT A GLANCE</div>
            <div className="t247-glance-list">
              <GlanceRow label="Data through device" value={fmt2(totalTrafficGB)} unit="GB" />
              <GlanceRow label="Carried on cellular" value={totalTrafficGB > 0 ? fmt1(cellularPct) : "—"} unit={totalTrafficGB > 0 ? "%" : ""} />
              <GlanceRow label="Failover events" value={String(failoverEvents)} />
            </div>
          </div>

          {/* WAN STATUS */}
          <div className="t247-panel" data-tour="mon-wan">
            <div className="t247-panel-header-row">
              <span>WAN STATUS &amp; USAGE</span>
              <span className="t247-muted-label">{downCount} DOWN</span>
            </div>
            {wanRows.length === 0 ? (
              <EmptyState text="No WAN data." />
            ) : (
              <>
                <div className="t247-scroll-block" style={{ maxHeight: 340 }}>
                  {wanRows.map((r, idx) => (
                    <div className="t247m-wan-card" key={r.key} onClick={() => setSelectedWan(r)} role="button" tabIndex={0} data-tour={idx === 0 ? "mon-first-wan" : undefined}>
                      <div className="row1">
                        <span className="t247-wan-dot" style={{ background: r.ledColor == 'red' ? "#D7D7CC" : r.ledColor == 'green' ? "#88F4EB" : "#CAFB6D" }} />
                        <span className="name">{r.name}</span>
                        <span style={{textTransform: 'uppercase', }} className={`t247-chip ${r.ledColor == 'red' ? "chip-down" : r.ledColor == 'green' ? "chip-up" : "chip-other"}`}>{r.statusLabel}</span>
                      </div>
                      <div className="row15">
                        <span className="ip">{r.ip}</span>
                        <span data-tour={idx === 0 ? "mon-first-prio" : undefined}>
                          <WanPriorityControl
                            row={r}
                            serialNumber={serialNumber}
                            managementType={managementType}
                            deviceName={deviceData?.deviceName}
                            scope={priorityScope}
                            enabled={canChangePriority && !r.synthesized}
                            onChanged={onPriorityChanged}
                            chipClass={r.ledColor == 'red' ? "chip-down" : r.ledColor == 'green' ? "chip-up" : "chip-other"}
                          />
                        </span>
                      </div>
                      <div className="row2">
                        <span>↓ {fmt2(r.download)} GB</span>
                        <span>↑ {fmt2(r.upload)} GB</span>
                        <span className="strong">Σ {fmt2(r.total)} GB</span>
                      </div>
                      <MiniBar pct={r.pct} color={r.isDown ? "rgba(255,255,255,.35)" : "#88F4EB"} />
                    </div>
                  ))}
                </div>
                <div className="t247-log-footer">
                  <span>TOTAL Σ {fmt2(wanTotals.total)} GB</span>
                  <span>TAP A CARD FOR DETAIL</span>
                </div>
              </>
            )}
          </div>

          {/* DATA USAGE GRAPH */}
          <div className="t247-panel" data-tour="mon-graph">
            <div className="t247-panel-header-row">
              <span>DATA USAGE GRAPH</span>
            </div>
            <div style={{ padding: 14 }}>
              {period === "daily" ? (
                (rawDeviceData?.hourlyWanUsagesLogs?.details && Object.keys(rawDeviceData.hourlyWanUsagesLogs.details).length > 0) ||
                (rawDeviceData?.dailyWanUsagesLogs?.details && Object.keys(rawDeviceData.dailyWanUsagesLogs.details).length > 0) ||
                (rawDeviceData?.monthlyWanUsagesLogs?.details && Object.keys(rawDeviceData.monthlyWanUsagesLogs.details).length > 0) ? (
                  <CombinedHourlyGraph
                    hourlyWanUsagesLogs={rawDeviceData.hourlyWanUsagesLogs}
                    dailyWanUsagesLogs={rawDeviceData.dailyWanUsagesLogs}
                    monthlyWanUsagesLogs={rawDeviceData.monthlyWanUsagesLogs}
                  />
                ) : (
                  <EmptyState text="No usage data." />
                )
              ) : (
                rawDeviceData?.dailyBreakdown?.length > 0 ? (
                  <DailyBreakdownChart
                    dailyBreakdown={rawDeviceData.dailyBreakdown}
                    period={period}
                  />
                ) : (
                  <EmptyState text="No usage data." />
                )
              )}
            </div>
          </div>

          {/* VLAN USAGE */}
          <div className="t247-panel" data-tour="mon-vlan">
            <div className="t247-panel-header-row">
              <span>VLAN USAGE</span>
              <span className="t247-muted-label">{deviceData.clientUsages.length} CLIENTS</span>
            </div>
            {vlanRows.length === 0 ? (
              <EmptyState text="No VLAN data." />
            ) : (
              vlanRows.map((v) => (
                <div className="t247-vlan-row" key={v.vlan} onClick={() => setSelectedVlan(v)} role="button" tabIndex={0}>
                  <span className="t247-vlan-name">{v.vlan} · {v.name}</span>
                  <span className="t247-vlan-clients">{v.clients}c</span>
                  <MiniBar pct={v.pct} color={v.pct > 50 ? "#D7D7CC" : "#88F4EB"} />
                  <span className="t247-num" style={{ width: 62 }}>{fmt2(v.total)} GB</span>
                </div>
              ))
            )}
          </div>

          {/* CLIENT USAGE */}
          <div className="t247-panel" data-tour="mon-clients">
            <div className="t247-panel-header-row">
              <span>CLIENT USAGE</span>
              <span className="t247-muted-label">{allClients.length} TOTAL</span>
            </div>
            {allClients.length === 0 ? (
              <EmptyState text="No client usage data." />
            ) : (
              <>
                <div className="t247-scroll-block" style={{ maxHeight: 340 }}>
                  {allClients.map((c, i) => (
                    <div className="t247m-wan-card" key={`${c.mac_address}-${i}`} style={{ cursor: "default" }}>
                      <div className="row1">
                        <span className="name">{c.name}</span>
                        <span className="t247-num strong" style={{ marginLeft: "auto" }}>{fmt2((c.total_usage || 0) / 1024)} GB</span>
                      </div>
                      <div className="ip">{c.ip_address} · VLAN {c.vlan}</div>
                      <div className="row2">
                        <span>↓ {fmt2((c.download || 0) / 1024)} GB</span>
                        <span>↑ {fmt2((c.upload || 0) / 1024)} GB</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="t247-log-footer">
                  <span>TOTAL</span>
                  <span>
                    ↓ {fmt2(clientTotals.download)} GB &nbsp;
                    ↑ {fmt2(clientTotals.upload)} GB &nbsp;
                    Σ {fmt2(clientTotals.total)} GB
                  </span>
                </div>
              </>
            )}
          </div>

          {/* WAN USAGE DISTRIBUTION */}
          <div className="t247-panel" data-tour="mon-wan-dist">
            <div className="t247-panel-header-row">
              <span>WAN DISTRIBUTION</span>
            </div>
            {wanRows.length === 0 ? (
              <EmptyState text="No WAN usage data." />
            ) : (
              <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 20 }}>
                <DonutChart segments={wanDownloadSegments} size={110} label="DOWNLOAD · GB" />
                <DonutChart segments={wanUploadSegments} size={110} label="UPLOAD · GB" />
              </div>
            )}
          </div>

          {/* PING MONITORING */}
          {/*
          {pingStats && (
            <div className="t247-panel" data-tour="mon-ping">
              <div className="t247-panel-header-row">
                <span>PING MONITORING</span>
                <span className="t247-muted-label">{pingStats.targets.length} TARGETS</span>
              </div>
              <div style={{ padding: "16px 18px 0" }}>
                <div className="t247-eyebrow" style={{ marginBottom: 6 }}>AVG RTT PER CYCLE (MS)</div>
                <AreaSpark values={pingStats.rttSeries} height={70} color="#88F4EB" />
              </div>
              {pingStats.targets.map((t) => (
                <div className="t247m-wan-card" key={t.target} style={{ cursor: "default" }}>
                  <div className="row1">
                    <span className="name">{t.target}</span>
                    <span className="t247-num" style={{ marginLeft: "auto" }}>{fmt1(t.successRate)}% OK</span>
                  </div>
                  <div className="row2">
                    <span>LOSS {fmt1(t.avgLoss)}%</span>
                    <span>RTT {t.avgRtt != null ? `${fmt1(t.avgRtt)}ms` : "—"}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          */}

          {/* REAL-TIME BANDWIDTH */}
          {period === "daily" && (
            <div className="t247-panel" data-tour="mon-realtime">
              <div className="t247-panel-header-row">
                <span>REAL-TIME BANDWIDTH</span>
                <span className="t247-secondary-label">LIVE</span>
              </div>
              <div style={{ padding: 14 }}>
                <RealtimeBandwidthChart serialNumber={serialNumber} />
              </div>
            </div>
          )}

          {/* SPEEDFUSION VPN LIVE STATUS — hides itself if no SpeedFusion peers */}
          {period === "daily" && <SpeedFusionLiveStatus serialNumber={serialNumber} />}

          {/* LOCATION MAP — Peplink GPS history, else Starlink live fallback */}
          {(hasPeplinkTrack || starlinkLocation) && (
            <div className="t247-panel" data-tour="mon-map">
              <div className="t247-panel-header-row">
                <span>{hasPeplinkTrack ? "LOCATION TRACKING" : "LOCATION"}</span>
                <span className="t247-muted-label">
                  {hasPeplinkTrack ? "PEPLINK · HISTORY" : "STARLINK · LIVE"}
                </span>
              </div>
              <div style={{ padding: 14 }}>
                <LeafletMap
                  locationMap={deviceData.locationMap}
                  liveLocation={hasPeplinkTrack ? null : starlinkLocation}
                  isHistoricalView={!isLiveView}
                />
              </div>
            </div>
          )}

          {/* LOGS */}
          <div className="t247-panel" data-tour="mon-logs">
            <div className="t247-panel-header-row">
              <span>LOGS &amp; ALERTS</span>
            </div>
            <div style={{ padding: 14 }}>
              <LogsDisplay
                pepVPNLogs={deviceData?.pepVPNLogs}
                firewallLogs={deviceData?.firewallLogs}
                wanLogs={deviceData?.wanLogs}
                systemLogs={deviceData?.systemLogs}
                dhcpLogs={deviceData?.dhcpLogs}
                adminLogs={deviceData?.adminLogs}
                alerts={alerts}
                vlanAlerts={vlanAlerts}
                wanCapAlerts={wanCapAlerts}
                starlinkAlerts={starlinkAlerts}
                isClient={isClient}
                shouldSkipStarlink={shouldSkipStarlink}
                period={period}
              />
            </div>
            <div className="t247-log-footer">
              <span>*Router local timezone</span>
            </div>
          </div>
        </div>
      )}

      {selectedWan && <T247WanModal row={selectedWan} deviceData={deviceData} onClose={() => setSelectedWan(null)} userType={userType} />}
      {selectedVlan && <T247VlanModal vlan={selectedVlan} onClose={() => setSelectedVlan(null)} />}

      <style dangerouslySetInnerHTML={{ __html: T247_THEME_CSS }} />
      <style dangerouslySetInnerHTML={{ __html: `
        .t247 {
          --color-card: var(--navy-800);
          --color-background: var(--navy-800);
          --color-surface: var(--navy-700);
          --color-border: var(--line);
          --color-label: var(--text-muted);
          --color-text-primary: #fff;
          --color-text-secondary: var(--text-dim);
          --color-primary: var(--navy-800);
          --color-download: #d7d7cc;
          --color-secondary: var(--turquoise);
          --color-upload: var(--turquoise);
          --color-success: var(--turquoise);
        }

        @media (min-width: 769px) { .t247m { display: none; } }

        .t247m-devicebar-identity .name { font: 700 15px var(--font-sans); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .t247m-devicebar-identity .serial { margin-top: 2px; font: 400 10.5px var(--font-mono); color: var(--text-dim); }

        .t247-back-btn {
          display: inline-flex; align-items: center; gap: 8px; margin-bottom: 12px; padding: 7px 12px;
          font: 400 11px var(--font-mono); letter-spacing: 0.06em; color: var(--text-dim);
          background: transparent; border: 1px solid rgba(255, 255, 255, 0.16); cursor: pointer;
        }
        .t247-back-btn:hover { color: var(--turquoise); border-color: var(--turquoise); }
        .t247-back-btn:focus-visible { outline: 2px solid var(--turquoise); outline-offset: 2px; }
        .t247m-devicebar { padding: 14px; background: var(--navy-800); border-bottom: 1px solid var(--line); }

        .t247m-stack { display: flex; flex-direction: column; gap: 12px; padding: 12px; }
        .t247m-stack .t247-panel { border: 1px solid var(--line); }

        .t247-panel-header { padding: 14px 18px; border-bottom: 1px solid var(--line); font: 400 10px var(--font-mono); letter-spacing: 0.14em; color: var(--text-dim); }
        .t247-panel-header.sub { margin-top: 4px; border-top: 1px solid var(--line); }

        .t247-state-grid { display: grid; grid-template-columns: 1fr 1fr; }
        .t247-state-cell { padding: 14px 18px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
        .t247-state-cell:nth-child(odd) { border-right: 1px solid rgba(255, 255, 255, 0.08); }
        .t247-state-cell .label { font: 400 9.5px var(--font-mono); letter-spacing: 0.12em; color: var(--text-muted); }
        .t247-state-cell .value { margin-top: 5px; font: 400 14px var(--font-mono); }
        .t247-state-cell .value.small { font-size: 12.5px; }

        .t247-glance-list { padding: 0 18px 16px; }
        .t247-glance-row { display: flex; align-items: baseline; gap: 10px; padding: 12px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
        .t247-glance-row.last { border-bottom: none; }
        .t247-glance-row .glabel { font: 400 11.5px var(--font-sans); color: var(--text-dim); flex: 1; }
        .t247-glance-row .gvalue { font: 700 16px var(--font-mono); }
        .t247-glance-row .gunit { font-size: 11px; font-weight: 400; color: var(--text-muted); margin-left: 4px; }

        .t247-flagged { padding: 14px 18px; background: var(--navy-700); border-left: 2px solid var(--neon); border: 1px solid var(--line); }
        .t247-flagged-text { margin-top: 7px; font-weight: 400; font-size: 12px; line-height: 1.55; font-family: var(--font-sans); }

        .t247-panel-header-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 14px 18px; border-bottom: 1px solid var(--line); font: 400 10px var(--font-mono); letter-spacing: 0.12em; color: var(--text-dim); }
        .t247-muted-label { font: 400 9.5px var(--font-mono); letter-spacing: 0.08em; color: var(--text-muted); }
        .t247-secondary-label { font: 400 9.5px var(--font-mono); letter-spacing: 0.08em; color: var(--color-secondary); }

        .t247m-wan-card { padding: 14px 18px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
        .t247m-wan-card .row1 { display: flex; align-items: center; gap: 8px; }
        .t247m-wan-card .name { font: 700 13px var(--font-sans); }
        .t247m-wan-card .row15 { margin-top: 3px; display: flex; align-items: center; gap: 8px; }
        .t247m-wan-card .ip { font: 400 10.5px var(--font-mono); color: var(--text-muted); }
        .t247m-wan-card .row2 { display: flex; gap: 12px; margin: 8px 0; font: 400 12px var(--font-mono); color: var(--text-dim); flex-wrap: wrap; }
        .t247m-wan-card .row2 .strong { color: #fff; font-weight: 700; }
        .t247-wan-dot { width: 7px; height: 7px; flex: none; }
        .t247-chip { padding: 2px 6px; font: 400 9px var(--font-mono); letter-spacing: 0.08em; margin-left: auto; }

        /* RealtimeBandwidthChart's "BANDWIDTH STATISTICS" table (t247-wan-headrow/row/name).
           Column widths always come from the chart's own inline gridTemplateColumns
           (equal 1fr columns), so only base look + a mobile-tightened gap live here. */
        .t247-wan-headrow, .t247-wan-row {
          display: grid; align-items: center; gap: 8px; padding: 9px 12px;
        }
        .t247-wan-headrow { background: var(--navy-700); border-bottom: 1px solid var(--line); font: 400 9px var(--font-mono); letter-spacing: 0.08em; color: var(--text-dim); }
        .t247-wan-row { border-bottom: 1px solid rgba(255, 255, 255, 0.08); cursor: pointer; }
        .t247-wan-row:hover { background: rgba(255, 255, 255, 0.03); }
        .t247-wan-name { font: 700 12px var(--font-sans); display: flex; align-items: center; gap: 6px; }

        .t247-throughput-legend { display: flex; gap: 16px; padding: 12px 18px 0; font: 400 12px var(--font-mono); }
        .t247-throughput-legend .muted { color: var(--text-dim); }

        .t247-talker-row, .t247-vlan-row { display: flex; align-items: center; gap: 12px; padding: 10px 18px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
        .t247-talker-name { flex: 1; font: 700 12px var(--font-sans); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .t247-talker-name .muted { font-weight: 400; font-family: var(--font-mono); font-size: 10.5px; color: var(--text-muted); }
        .t247-vlan-name { font: 700 12px var(--font-sans); flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .t247-vlan-clients { font: 400 11px var(--font-mono); color: var(--text-muted); }
        .t247-num { font: 400 12px var(--font-mono); text-align: right; }
        .t247-num.strong { font-weight: 700; }

        .t247-statbox-grid { display: grid; gap: 1px; background: var(--line); margin: 14px 18px 0; border: 1px solid var(--line); }
        .t247-statbox { background: var(--navy-800); padding: 12px 14px; }
        .t247-statbox .label { font: 400 9px var(--font-mono); letter-spacing: 0.1em; color: var(--text-muted); }
        .t247-statbox .value { margin-top: 6px; font: 700 18px var(--font-mono); }
        .t247-statbox .unit { font-size: 10px; font-weight: 400; color: var(--text-muted); margin-left: 4px; }

        .t247-period-btn { padding: 9px 14px; font: 400 11px var(--font-mono); letter-spacing: 0.06em; color: var(--text-dim); background: transparent; border: none; cursor: pointer; }
        .t247-period-btn.active { background: var(--turquoise); color: var(--navy-800); }

        .t247-logs-tabs { display: flex; gap: 2px; padding: 14px 18px 0; }
        .t247-logs-tab { flex: 1; padding: 8px 6px; font: 400 10px var(--font-mono); letter-spacing: 0.05em; color: var(--text-dim); background: transparent; border: none; cursor: pointer; }
        .t247-logs-tab.active { background: var(--turquoise); color: var(--navy-800); }
        .t247-log-list { margin-top: 12px; border-top: 1px solid rgba(255, 255, 255, 0.08); }
        .t247-log-row { display: flex; gap: 10px; padding: 11px 18px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
        .t247-log-time { font: 400 10px var(--font-mono); color: var(--text-muted); padding-top: 1px; flex: none; }
        .t247-log-level { padding: 1px 6px; height: 15px; background: rgba(255, 255, 255, 0.13); font: 400 9px var(--font-mono); letter-spacing: 0.06em; color: var(--text-dim); flex: none; }
        .t247-log-level.warn { background: var(--neon); color: var(--navy-800); }
        .t247-log-text { flex: 1; font-weight: 400; font-size: 11px; line-height: 1.5; font-family: var(--font-mono); word-break: break-word; }
        .t247-log-footer { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 12px 18px; font: 400 9.5px var(--font-mono); letter-spacing: 0.03em; color: var(--text-muted); }
        .t247-scroll-block { overflow-y: auto; }
        .t247-scroll-block::-webkit-scrollbar { width: 5px; }
        .t247-scroll-block::-webkit-scrollbar-track { background: transparent; }
        .t247-scroll-block::-webkit-scrollbar-thumb { background: rgba(136, 244, 235, 0.25); }
        @keyframes t247spin { to { transform: rotate(360deg); } }
        .t247-empty { padding: 24px 18px; text-align: center; font: 400 11px var(--font-mono); color: var(--text-muted); }
      ` }} />
    </div>
  );
}

const StateCell = ({ label, value, small }) => (
  <div className="t247-state-cell">
    <div className="label">{label}</div>
    <div className={`value ${small ? "small" : ""}`}>{value}</div>
  </div>
);

const GlanceRow = ({ label, value, unit, last }) => (
  <div className={`t247-glance-row ${last ? "last" : ""}`}>
    <span className="glabel">{label}</span>
    <span className="gvalue">
      {value} {unit && <span className="gunit">{unit}</span>}
    </span>
  </div>
);

const StatBox = ({ label, value, unit }) => (
  <div className="t247-statbox">
    <div className="label">{label}</div>
    <div className="value">
      {value} {unit && <span className="unit">{unit}</span>}
    </div>
  </div>
);

const EmptyState = ({ text }) => <div className="t247-empty">{text}</div>;

export default MobileLayout;
